import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  FluentProvider,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { formatDate } from '@moko/utils';
import { useActiveTheme } from '../theme/ThemeContext';
import { useIsMobile } from '../hooks/useIsMobile';
import useFetch from '../hooks/useFetch';
import DataGridUI, { type DataGridColumn } from '../DataGrid/DataGridUI';
import Empty from '../Empty/Empty';
import type {
  IWorkAppointmentListItem,
  IWorkAppointmentsServices,
  WorkComponentProps,
} from './types';
import ContentSkeleton from '../ContentSkeleton/ContentSkeleton';

type AppointmentColumn = DataGridColumn<IWorkAppointmentListItem> & {
  value: (item: IWorkAppointmentListItem) => string;
};

export interface WorkAppointmentsProps extends WorkComponentProps {
  workId: number;
  services: IWorkAppointmentsServices;
}

const useStyles = makeStyles({
  cards: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXXS,
  },
  label: {
    color: tokens.colorNeutralForeground3,
  },
});

const localDateTime = (local?: string | null, utc?: string) =>
  local ? local.slice(0, 16).replace('T', ' ') : formatDate(utc, true);

const WorkAppointments = ({
  workId,
  services,
  setIsInProgress,
  setErrorMessage,
}: WorkAppointmentsProps) => {
  const { t } = useTranslation();
  const theme = useActiveTheme();
  const isMobile = useIsMobile();
  const styles = useStyles();
  const appointmentsFetch = useFetch<IWorkAppointmentListItem[]>({
    queryKey: [workId, 'appointments'],
    queryFn: async (abortController) =>
      (await services.getWorkAppointments(abortController, workId))
        .appointments,
    onLoading: () => setIsInProgress(true),
    onLoaded: () => setIsInProgress(false),
    onError: (error) => {
      setIsInProgress(false);
      setErrorMessage(
        error.message ||
          t('failedToLoadAppointments', 'Failed to load appointments.')
      );
    },
  });

  const columns: AppointmentColumn[] = useMemo(
    () => [
      {
        key: 'localStartAt',
        name: t('start', 'Start'),
        width: 170,
        value: (item) => localDateTime(item.localStartAt, item.startAt),
      },
      {
        key: 'localEndAt',
        name: t('end', 'End'),
        width: 170,
        value: (item) => localDateTime(item.localEndAt, item.endAt),
      },
      {
        key: 'visitType',
        name: t('visitType', 'Visit type'),
        width: 130,
        value: (item) => item.visitType,
      },
      {
        key: 'status',
        name: t('status', 'Status'),
        width: 120,
        value: (item) => item.status,
      },
      {
        key: 'confirmationMethod',
        name: t('confirmationMethod', 'Confirmation method'),
        width: 170,
        value: (item) => item.confirmationMethod ?? '',
      },
      {
        key: 'acceptedAt',
        name: t('acceptedAt', 'Accepted at'),
        width: 170,
        value: (item) => formatDate(item.acceptedAt ?? undefined, true),
      },
    ],
    [t]
  );

  const items = useMemo(
    () => [...(appointmentsFetch.data ?? [])].sort((a, b) => b.id - a.id),
    [appointmentsFetch.data]
  );

  const empty = {
    title: t('noAppointmentsYet', 'No appointments yet!'),
    subtitle: t(
      'noAppointmentsYetHint',
      'Appointments scheduled for this work will appear here.'
    ),
  };

  if (isMobile) {
    if (!appointmentsFetch.data) {
      return appointmentsFetch.loading ? (
        <ContentSkeleton variant="list" />
      ) : null;
    }
    if (items.length === 0) {
      return <Empty title={empty.title} description={empty.subtitle} />;
    }
    return (
      <FluentProvider theme={theme} style={{ background: 'transparent' }}>
        <div className={styles.cards}>
          {items.map((item) => (
            <Card key={item.id} appearance="outline" size="small">
              {columns
                .filter((column) => column.value(item) !== '')
                .map((column) => (
                  <div key={column.key} className={styles.row}>
                    <Text size={200} className={styles.label}>
                      {column.name}
                    </Text>
                    <Text>{column.value(item)}</Text>
                  </div>
                ))}
            </Card>
          ))}
        </div>
      </FluentProvider>
    );
  }

  return (
    <FluentProvider theme={theme} style={{ background: 'transparent' }}>
      <DataGridUI<IWorkAppointmentListItem>
        items={items}
        columns={columns}
        loading={appointmentsFetch.loading && !appointmentsFetch.data}
        getRowId={(item) => item.id}
        empty={appointmentsFetch.data ? empty : undefined}
      />
    </FluentProvider>
  );
};

export default WorkAppointments;
